import React, { useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { 
  ArrowRight, Code, Layout, Terminal,
  Check, Shield, Server, Database, GitBranch,
  Brain
} from 'lucide-react';

const JobRequirement = ({ children }) => (
  <div className="flex items-start gap-3 group">
    <div className="p-1 rounded bg-[#017D7E]/10 group-hover:bg-[#017D7E]/20 transition-colors">
      <Check className="w-4 h-4 text-[#017D7E]" />
    </div>
    <span className="text-white/70 group-hover:text-white transition-colors">{children}</span>
  </div>
);

const TechBadge = ({ children }) => (
  <span className="px-4 py-1.5 rounded-full bg-[#017D7E]/10 text-[#017D7E] text-sm border border-[#017D7E]/20">
    {children}
  </span>
);

const TechStack = ({ icon: Icon, title, items }) => (
  <div className="flex items-start gap-3 group">
    <div className="p-2 rounded-lg bg-[#017D7E]/10 flex-shrink-0 group-hover:bg-[#017D7E]/20 transition-all">
      <Icon className="w-5 h-5 text-[#017D7E]" />
    </div>
    <div>
      <h3 className="font-medium text-white mb-1">{title}</h3>
      <p className="text-sm text-white/70">{items}</p>
    </div>
  </div>
);

const FullstackPage = () => {
  const navigate = useNavigate();

  // Animation background styles
  useEffect(() => {
    const styleSheet = document.createElement("style");
    styleSheet.innerText = `
      @keyframes float {
        0%, 100% { transform: translateY(0); }
        50% { transform: translateY(-10px); }
      }
      @keyframes pulse {
        0%, 100% { opacity: 0.5; }
        50% { opacity: 1; }
      }
    `;
    document.head.appendChild(styleSheet);
    return () => styleSheet.remove();
  }, []);

  return (
    <div className="min-h-screen text-white relative pt-20 pb-12">
      {/* Background effects */}
      <div className="fixed inset-0 -z-10">
        <div className="absolute inset-0 bg-[radial-gradient(circle_at_top,rgba(1,125,126,0.15),transparent_50%)]" />
        <div className="absolute inset-0 bg-[radial-gradient(circle_at_center_left,rgba(0,169,157,0.1),transparent_50%)]" />
      </div>

      <div className="container mx-auto px-4 relative z-10">
        <div className="max-w-4xl mx-auto">
          {/* Header section */}
          <div className="bg-white/5 backdrop-blur-sm rounded-xl p-8 border border-white/10 mb-8">
            <h1 className="text-3xl md:text-4xl font-bold mb-6">
              Développeur Fullstack
              <span className="block text-transparent bg-clip-text bg-gradient-to-r from-[#017D7E] to-[#00A99D]">
                Architect Solutions Financières
              </span>
            </h1>

            <div className="flex flex-wrap gap-4 mb-6 text-white/70">
              <div className="flex items-center gap-2">
                <Code className="w-5 h-5 text-[#017D7E]" />
                <span>100% Remote</span>
              </div>
              <div className="flex items-center gap-2">
                <ArrowRight className="w-5 h-5 text-[#017D7E]" />
                <span>Synchro hebdo à Casablanca</span>
              </div>
            </div>

            <div className="flex flex-wrap gap-3">
              <TechBadge>React</TechBadge>
              <TechBadge>Spring Boot</TechBadge>
              <TechBadge>PostgreSQL</TechBadge>
              <TechBadge>REST API</TechBadge>
              <TechBadge>Git</TechBadge>
            </div>
          </div>

          {/* Main content */}
          <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
            <div className="md:col-span-2 space-y-6">
              <section className="bg-white/5 backdrop-blur-sm rounded-xl p-6 border border-white/10">
                <h2 className="text-xl font-bold mb-4">Mission</h2>
                <p className="text-white/70 mb-6">
                  Rejoignez notre équipe pour développer des solutions financières innovantes destinées au marché africain. 
                  Vous participerez à la transformation digitale du secteur bancaire en travaillant sur l'ensemble de la stack technique.
                </p>
                <div className="space-y-4">
                  <JobRequirement>
                    Développement fullstack React.js et Spring Boot
                  </JobRequirement>
                  <JobRequirement>
                    Conception d'APIs RESTful performantes
                  </JobRequirement>
                  <JobRequirement>
                    Optimisation des performances applicatives
                  </JobRequirement>
                  <JobRequirement>
                    Code reviews et amélioration continue
                  </JobRequirement>
                </div>
              </section>

              <section className="bg-white/5 backdrop-blur-sm rounded-xl p-6 border border-white/10">
                <h2 className="text-xl font-bold mb-4">Profil</h2>
                <div className="space-y-4">
                  <JobRequirement>
                    3+ ans d'expérience fullstack
                  </JobRequirement>
                  <JobRequirement>
                    Expertise React.js et Spring Boot
                  </JobRequirement>
                  <JobRequirement>
                    Pratique des BDD SQL/NoSQL
                  </JobRequirement>
                  <JobRequirement>
                    Méthodologies Agile/Scrum
                  </JobRequirement>
                  <JobRequirement>
                    Anglais technique
                  </JobRequirement>
                </div>
              </section>
            </div>

            <div className="space-y-6">
              <section className="bg-white/5 backdrop-blur-sm rounded-xl p-6 border border-white/10">
                <h2 className="text-lg font-bold mb-4">Stack Technique</h2>
                <div className="space-y-4">
                  <TechStack 
                    icon={Layout}
                    title="Frontend"
                    items="React.js, TypeScript"
                  />
                  <TechStack
                    icon={Terminal}
                    title="Backend" 
                    items="Spring Boot, Java"
                  />
                  <TechStack
                    icon={Database}
                    title="Base de données"
                    items="PostgreSQL, MongoDB"
                  />
                  <TechStack
                    icon={GitBranch}
                    title="DevOps"
                    items="Git, Docker, Jenkins"
                  />
                </div>
              </section>

              <div className="sticky top-6">
                <div className="bg-gradient-to-r from-[#017D7E]/10 to-[#00A99D]/10 rounded-lg p-6 border border-[#017D7E]/20">
                  <h3 className="text-lg font-medium text-white mb-3">Pour postuler</h3>
                  <p className="text-white/70 mb-4">
                    Envoyez votre CV à :
                  </p>
                  <div className="bg-white/5 rounded-lg p-3 border border-white/10">
                    <p className="font-mono text-sm text-[#017D7E]">contact@kg-technologies.fr</p>
                    <p className="text-sm text-white/50 mt-2">Objet : Candidature Développeur Fullstack</p>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default FullstackPage;